import { styled } from "@linaria/react";
import cx from "classnames";
import React from "react";
import ReactModal, { type Props as ReactModalProps } from "react-modal";
import { easingSoft, easingSoftMs, zIndex, radius } from "..";
// import ErrorBoundary from "../ErrorBoundary";
// import Icon from "../Icon";
import { CloseLine } from "../IconV2/react";
import LoadingBoundary from "../LoadingBoundary";
import { colors } from "../Theme";
import { shadows } from "../Theme/shadows";
import ButtonIconV2 from "../ButtonIconV2";
import { css } from "@linaria/core";
import Text, { H4 } from "../Text";

export const MODAL_ID = "modal-id";

// if (process.env["NODE_ENV"] !== "test") ReactModal.setAppElement("#root");

const XLARGE_MODAL_WIDTH = 800;
const LARGE_MODAL_WIDTH = 584;
const MEDIUM_MODAL_WIDTH = 460;

const XLARGE_CN = "xlarge";
const LARGE_CN = "large";
const MEDIUM_CN = "medium";
const FULL_CN = "full";
const FILL_HEIGHT_CN = "fill-height";

type LinariaModalProps = Omit<ReactModalProps, "className"> & { className?: string };

const StyledReactModal = styled(ReactModal as unknown as React.ComponentType<LinariaModalProps>)`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 24px 32px;

  will-change: opacity, transform;
  transition:
    opacity ${easingSoftMs}ms ${easingSoft},
    transform ${easingSoftMs}ms ${easingSoft};
  background: ${colors.hydrogen};
  outline: none;
  cursor: default;
  color: ${colors.carbon900};
  width: 100%;
  // used for full viewport chart (body -> none when active)
  pointer-events: auto;

  @media (min-width: ${LARGE_MODAL_WIDTH}px) and (min-height: 350px) {
    flex: none;
    align-self: flex-start;
    height: auto;
    max-height: calc(100% - 10vw);
    box-shadow: ${shadows.shadow400};
    border-radius: ${radius};
    margin: 5vw auto 5vw auto;

    &.${FULL_CN} {
      width: calc(100% - 10vw);
    }

    &.${LARGE_CN} {
      width: ${LARGE_MODAL_WIDTH}px;
    }

    &.${XLARGE_CN} {
      width: min(calc(100% - 40px), ${XLARGE_MODAL_WIDTH}px);
    }

    &.${MEDIUM_CN} {
      width: ${MEDIUM_MODAL_WIDTH}px;
    }
    &.${FILL_HEIGHT_CN} {
      height: calc(100% - 10vw);
    }
  }
  &.${FILL_HEIGHT_CN} {
    height: 100%;
  }
`;

export type ModalSize = "m" | "l" | "xl" | "full";

export interface ModalProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  isOpen: boolean;
  closable?: boolean;
  onRequestClose: () => void;
  onAfterClose?: () => void;
  className?: string;
  size?: ModalSize;
  fillHeight?: boolean;
}

const Modal = ({
  className,
  title,
  children,
  isOpen,
  closable = true,
  onRequestClose,
  onAfterClose,
  subtitle,
  size = "m",
  fillHeight = false,
}: ModalProps) => {
  const [visible, setVisible] = React.useState(isOpen);
  const handleAfterClose = React.useCallback(() => {
    setVisible(false);
    onAfterClose?.();
  }, [onAfterClose]);
  React.useLayoutEffect(() => {
    if (isOpen) setVisible(true);
  }, [isOpen]);

  const style = React.useMemo(
    () => ({
      overlay: {
        willChange: "opacity",
        transition: `opacity ${easingSoftMs}ms ${easingSoft}`,
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: colors.modalOverlayBackground,
        cursor: closable ? "pointer" : "not-allowed",
        display: "flex",
        zIndex: zIndex.modalOverlay,
      } as const,
    }),
    [closable],
  );

  return (
    <StyledReactModal
      id={MODAL_ID}
      className={cx(className, {
        [XLARGE_CN]: size === "xl",
        [LARGE_CN]: size === "l",
        [MEDIUM_CN]: size === "m",
        [FULL_CN]: size === "full",
        [FILL_HEIGHT_CN]: fillHeight,
      })}
      closeTimeoutMS={easingSoftMs}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      onAfterClose={handleAfterClose}
      shouldCloseOnOverlayClick={closable}
      shouldCloseOnEsc={closable}
      style={style}
      ariaHideApp={false}
    >
      <header className={cx(modalHeader, className)}>
        {title !== undefined && <H4 className="title">{title}</H4>}
        {closable && (
          <ButtonIconV2 onClick={onRequestClose} tabIndex={-1} className="close-button">
            <CloseLine size="l" color={colors.titanium800} />
          </ButtonIconV2>
        )}
        {subtitle !== undefined && typeof subtitle === "string" && (
          <Text className="subtitle" type="body1">
            {subtitle}
          </Text>
        )}
      </header>

      <LoadingBoundary>{visible ? children : null}</LoadingBoundary>
    </StyledReactModal>
  );
};

export default React.memo(Modal);

const modalHeader = css`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px;
  align-items: center;

  & > .title {
    color: ${colors.carbon900};
    overflow-wrap: break-word;
    hyphens: auto;
    width: 100%;
  }

  & > .subtitle {
    color: ${colors.carbon700};
    grid-column: 1 / -1;
    grid-row: 2;
  }

  & > .close-button {
    align-self: start;
    height: 20px;
    width: 20px;
    grid-row: 1 / span 2;
    grid-column: 2 / -1;
  }
`;
